import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import client, { formatApiError } from "../lib/authApi";
import { toast } from "sonner";

/**
 * Forgot-password page, collects an email and triggers /api/auth/forgot-password.
 * Always shows a success state (anti-enumeration: the API returns 200 either way).
 * P0.5A M4 — Se a API retornar `unavailable=true`, mostramos mensagem clara
 * de indisponibilidade temporária, sem simular sucesso falso.
 */
export default function EsqueciSenha() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await client.post("/api/auth/forgot-password", { email: email.trim() });
      const data = r?.data || {};
      setUnavailable(Boolean(data.unavailable));
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto section-padding py-10" data-testid="esqueci-senha-page">
      <Link
        to="/entrar"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="esqueci-back"
      >
        <ArrowLeft className="w-4 h-4" /> Entrar
      </Link>

      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Recuperar acesso</p>
      <h1 className="font-display text-4xl text-navy mt-2 leading-tight">
        Esqueci minha senha
      </h1>
      <p className="text-navy/65 mt-3 text-sm">
        Enviaremos um link para você redefinir sua senha. O link expira em 1 hora.
      </p>

      {sent && unavailable ? (
        <div
          className="mt-8 bg-amber-50 border border-amber-300 rounded-2xl p-6"
          data-testid="esqueci-unavailable"
        >
          <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-3" />
          <p className="font-display text-xl text-navy text-center">Envio de e-mail temporariamente indisponível</p>
          <p className="text-sm text-navy/70 mt-3 leading-relaxed">
            Estamos ajustando a entrega de e-mails transacionais. No momento não conseguimos disparar o link de redefinição para você. Tente novamente em algumas horas ou entre em contato com o pastor pelo WhatsApp, e ele redefine sua senha manualmente.
          </p>
          <a
            href="https://api.whatsapp.com/send?phone=558181739909&text=Ol%C3%A1%2C%20preciso%20redefinir%20minha%20senha%20do%20aplicativo."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="esqueci-whatsapp-fallback"
            className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
          >
            Falar com o pastor
          </a>
          <Link
            to="/entrar"
            className="mt-3 block text-center text-[11px] uppercase tracking-widest text-navy/65 hover:text-gold"
            data-testid="esqueci-back-to-login-unavail"
          >
            Voltar para o login
          </Link>
        </div>
      ) : sent ? (
        <div
          className="mt-8 bg-[hsl(var(--parchment))] border border-gold/40 rounded-2xl p-6 text-center"
          data-testid="esqueci-success"
        >
          <CheckCircle2 className="w-10 h-10 text-gold mx-auto mb-3" />
          <p className="font-display text-xl text-navy">Verifique seu e-mail</p>
          <p className="text-sm text-navy/65 mt-2 leading-relaxed">
            Se <span className="font-medium text-navy">{email}</span> estiver cadastrado, você receberá em alguns instantes um link para redefinir sua senha. Confira também a caixa de spam.
          </p>
          <Link
            to="/entrar"
            className="inline-block mt-5 text-[11px] uppercase tracking-widest text-navy/65 hover:text-gold"
            data-testid="esqueci-back-to-login"
          >
            Voltar para o login
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="esqueci-form">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-navy/55">E-mail cadastrado</span>
            <div className="mt-2 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                data-testid="esqueci-email"
                className="w-full pl-10 pr-3 py-3 rounded-xl border border-border bg-white text-navy/90 focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={submitting || !email.trim()}
            data-testid="esqueci-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
          >
            {submitting ? "Enviando..." : "Enviar link de redefinição"}
          </button>

          <p className="text-[12px] text-navy/55 text-center pt-1">
            Lembrou da senha?{" "}
            <Link to="/entrar" className="text-navy hover:text-gold" data-testid="esqueci-login-link">
              Voltar para o login
            </Link>
          </p>
        </form>
      )}
    </div>
  );
}
